// notificationStore.ts
import { create } from 'zustand'
import { Transition } from '@headlessui/react'
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react'
import React from 'react'
import { createPortal } from 'react-dom'

export enum NotificationType {
    SUCCESS = 'success',
    ERROR = 'error',
    INFO = 'info',
    WARNING = 'warning'
}

const NotificationIcon = {
    [NotificationType.SUCCESS]: (props: any) => <CheckCircle {...props} className="h-5 w-5 text-green-500" />,
    [NotificationType.ERROR]: (props: any) => <XCircle {...props} className="h-5 w-5 text-red-500" />,
    [NotificationType.INFO]: (props: any) => <Info {...props} className="h-5 w-5 text-blue-500" />,
    [NotificationType.WARNING]: (props: any) => <AlertTriangle {...props} className="h-5 w-5 text-yellow-500" />
}

const NotificationStyles = {
    [NotificationType.SUCCESS]: 'border-green-500 bg-green-50',
    [NotificationType.ERROR]: 'border-red-500 bg-red-50',
    [NotificationType.INFO]: 'border-blue-500 bg-blue-50',
    [NotificationType.WARNING]: 'border-yellow-500 bg-yellow-50'
}

type Notification = { id: string; type: NotificationType; message: string }

type NotiState = {
    items: Notification[]
    showNotification: (type: NotificationType, message: string, ttl?: number) => void
    remove: (id: string) => void
}

export const useNotificationStore = create<NotiState>((set, get) => ({
    items: [],
    showNotification: (type, message, ttl = 5000) => {
        const id = Math.random().toString(36).slice(2)
        set(s => ({ items: [...s.items, { id, type, message }] }))
        if (ttl > 0) setTimeout(() => get().remove(id), ttl)
    },
    remove: id => set(s => ({ items: s.items.filter(n => n.id !== id) }))
}))
export const nonReactShowNotification = (type: NotificationType, message: string) =>
    useNotificationStore.getState().showNotification(type, message);
// Hook consumers will use
export function useNotification() {
    const showNotification = useNotificationStore(s => s.showNotification)
    return { showNotification }
}

// A viewport you mount once at the app root
export function NotificationViewport() {
    const items = useNotificationStore(s => s.items)
    const remove = useNotificationStore(s => s.remove)

    if (typeof document === 'undefined') return null

    return createPortal(
        <div className="fixed top-4 right-4 z-[1000] space-y-2 min-w-[320px] max-w-md">
            {items.map(n => (
                <Transition
                    key={n.id}
                    show
                    appear
                    enter="transform ease-out duration-300 transition"
                    enterFrom="translate-y-[-1rem] opacity-0"
                    enterTo="translate-y-0 opacity-100"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className={`rounded-lg shadow-lg border p-4 ${NotificationStyles[n.type]}`}>
                        <div className="flex items-start">
                            <div className="flex-shrink-0">{React.createElement(NotificationIcon[n.type])}</div>
                            <div className="ml-3 w-0 flex-1">
                                <p className="text-sm text-slate-700">{n.message}</p>
                            </div>
                            <div className="ml-4 flex-shrink-0 flex">
                                <button
                                    className="inline-flex text-slate-400 hover:text-slate-500 focus:outline-none
                           focus:ring-2 focus:ring-slate-500 rounded-full p-0.5"
                                    onClick={() => remove(n.id)}
                                >
                                    <span className="sr-only">Close</span>
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </Transition>
            ))}
        </div>,
        document.body
    )
}
